// src/components/layout/Topbar.tsx
import React, { useEffect, useState } from "react";
import { useThemeMode } from "../../context/ThemeModeContext";
import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    IconButton,
    Menu,
    MenuItem,
    Divider,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { AccountCircle, ArrowDropDown } from "@mui/icons-material";
import { STORAGE_KEYS } from "../../utils/StorageKeys";
import { styled } from "@mui/material/styles"
import { brandPrimary, brandPrimaryDark } from "../../theme";
import useDeviceDetection from "../../utils/useDeviceDetection";


interface TopbarProps {
    isLoggedIn: boolean;
    isAdmin?: boolean;
    onLogout?: () => void;
    isDarkMode?: boolean;
    onToggleTheme?: () => void;
}

const GradientHeader = styled(Box)`
  background: linear-gradient(135deg,${brandPrimary} 0%,${brandPrimaryDark} 100%);
`
/* -- Logos (white-tinted via CSS filter) -- */
export const Logo = styled("img")`
  height: 35px; 
  width: auto; 
  object-fit: contain; 
  filter: brightness(0) invert(1);
  
  @media (max-width: 768px) {
    height: 28px;
  }
  
  @media (max-width: 480px) {
    height: 24px;
  }
`
const LogoDigital = styled(Logo)`
  height: 50px;
  
  @media (max-width: 768px) {
    height: 40px;
  }
  
  @media (max-width: 480px) {
    height: 32px;
  }
`

const Topbar: React.FC<TopbarProps> = ({ isLoggedIn, isAdmin, onLogout }) => {
    const { isDarkMode, toggleDarkMode } = useThemeMode();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [name, setName] = useState<string>("User");
    const navigate = useNavigate();
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const { isMobile, isTablet, isDesktop } = useDeviceDetection();

    useEffect(() => {
        if (isLoggedIn) {
            const userData = localStorage.getItem(STORAGE_KEYS.USER);
            if (userData) {
                try {
                    const parsed = JSON.parse(userData);
                    setName(parsed.name || "User");
                } catch (e) {
                    setName("User");
                }
            }
        }
    }, [isLoggedIn]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    if (!accessToken) {
        return (null);
    }

    return (
        <GradientHeader>
            <AppBar position="fixed" elevation={0} sx={{}}>
                <Toolbar sx={{
                    gap: isMobile ? 1 : 3,
                    alignItems: "center",
                    px: isMobile ? 1 : 3,
                    minHeight: isMobile ? "56px" : "64px",
                    flexGrow: 1, display: 'flex',
                }}>

                    {!isMobile ? (
                        <>
                            <Logo
                                src="/images/logo1.png"
                                alt="Thoughtline"
                                onError={(e) => (e.currentTarget.style.display = "none")}
                            />
                            <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,.4)" }} />
                            <LogoDigital
                                src="/images/logo2.png"
                                alt="Thoughtline digital"
                                onError={(e) => (e.currentTarget.style.display = "none")}
                            />
                        </>
                    ) : <Logo
                        src="ThoughtDeskLogo.png"
                        alt="Thoughtline"
                        onError={(e) => (e.currentTarget.style.display = "none")}
                    />}
                    {/* Right side - Contact button or Login/Logout */}
                    <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
                        {isLoggedIn ? (
                            <>
                                <IconButton
                                    size={isMobile ? "medium" : "large"}
                                    edge="end"
                                    color="inherit"
                                    aria-label="menu"
                                    onClick={handleMenuOpen}
                                    sx={{
                                        borderRadius: 2,
                                        px: isMobile ? 1 : 2,
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                        },
                                    }}
                                >
                                    <AccountCircle sx={{ mr: isMobile ? 0.5 : 1 }} />
                                    {!isMobile && (
                                        <Typography variant="body1" sx={{ textTransform: "none", fontWeight: 500 }}>
                                            {name}
                                        </Typography>
                                    )}
                                    <ArrowDropDown />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "right",
                                    }}
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "right",
                                    }}
                                >
                                    {isLoggedIn && (
                                        <MenuItem onClick={() => { handleMenuClose(); navigate("/reset-password"); }}>
                                            Reset Password
                                        </MenuItem>
                                    )}
                                    <MenuItem onClick={() => { handleMenuClose(); onLogout && onLogout(); }}>
                                        Logout
                                    </MenuItem>
                                </Menu>
                                {/* Theme toggle switch */}
                                <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: "rgba(255,255,255,.4)" }} />
                                <IconButton
                                    color="inherit"
                                    aria-label="toggle theme"
                                    onClick={toggleDarkMode}
                                    sx={{ ml: 1 }}
                                >
                                    {isDarkMode ? (
                                        <span role="img" aria-label="dark mode">🌙</span>
                                    ) : (
                                        <span role="img" aria-label="light mode">☀️</span>
                                    )}
                                </IconButton>
                            </>
                        ) : (
                            <MenuItem component={RouterLink} to="/login" onClick={handleMenuClose}>
                                Login
                            </MenuItem>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>
        </GradientHeader>
    );
};

export default Topbar;